import { SIGNIN_SUPPORT_TOPICS, type SupportTopic } from "@iec-id/shared/contracts/support";
import { Button, Dialog, SupportForm, type SupportFormValues, useSupportUpload } from "@iec-id/ui";
import { useState } from "react";
import { usePublicLegalManifest } from "@/components/legal/public-legal";
import { useLocale } from "@/lib/locale";
import { api } from "@/lib/trpc";
import { useSupportT } from "./support-i18n";

/**
 * Ключ документа, согласие на который фиксируется вместе с обращением.
 * Совпадает с реестром Legal Center — пара `(version, sha256)` берётся оттуда
 * же, а не вычисляется на клиенте.
 */
const PRIVACY_KEY = "privacy-policy";

export type SupportDialogContext = {
	/** Сервис, из которого человек пришёл, — уходит вместе с обращением. */
	readonly serviceId?: string | null;
	/** Код с экрана ошибки входа: по нему дежурный находит прогон в логах. */
	readonly errorCode?: string | null;
	/** Почта, уже введённая на экране входа: не заставляем набирать дважды. */
	readonly accountEmail?: string | null;
};

/**
 * 429 распознаётся по КОДУ ошибки, а не по её тексту.
 *
 * `TRPCError.message` — человеческая фраза, и она меняется вместе с
 * формулировкой на сервере; код лежит в `data.code`. Пока проверка шла по
 * подстроке в тексте, человек, упёршийся в лимит, видел общее «не удалось
 * отправить» и жал кнопку снова — то есть получал неверную причину вместо
 * честного «слишком часто» (грабли №32: конкретная, но неправильная причина
 * хуже отсутствующей).
 */
function isTooManyRequests(error: unknown): boolean {
	const data = (error as { data?: { code?: string; httpStatus?: number } }).data;
	return data?.code === "TOO_MANY_REQUESTS" || data?.httpStatus === 429;
}

export function SupportDialog({
	open,
	onOpenChange,
	context,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	context?: SupportDialogContext;
}) {
	const t = useSupportT();
	const locale = useLocale();
	const [ticketCode, setTicketCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [honeypot, setHoneypot] = useState("");

	const manifest = usePublicLegalManifest(locale);
	const policy = manifest.data?.documents.find((document) => document.key === PRIVACY_KEY);

	const createDraft = api.support.createDraft.useMutation();
	const attachmentUrl = api.support.attachmentUrl.useMutation();

	const upload = useSupportUpload({
		locale,
		// Порт подставляет приложение: кит не знает про наш tRPC-роутер.
		port: {
			createDraft: () => createDraft.mutateAsync({}),
			requestUpload: (request) => attachmentUrl.mutateAsync(request),
		},
		errors: {
			tooMany: t("files.errors.tooMany"),
			tooLarge: t("files.errors.tooLarge"),
			wrongType: t("files.errors.wrongType"),
			failed: t("files.errors.failed"),
		},
	});

	const submit = api.support.submit.useMutation();

	const close = (next: boolean) => {
		onOpenChange(next);
		// Сброс — только на закрытии: пока окно открыто, человек может
		// вернуться к форме, и потерянный текст обращения он второй раз не
		// наберёт.
		if (!next) {
			setTicketCode(null);
			setError(null);
			setHoneypot("");
			upload.reset();
		}
	};

	const onSubmit = async (values: SupportFormValues) => {
		setError(null);

		// Документ не загрузился — форма не притворяется, что согласие
		// получено: без пары `(version, sha256)` сервер всё равно откажет, а
		// человек увидел бы непонятную ошибку уже после отправки.
		if (!policy) {
			setError(t("errors.policyUnavailable"));
			return;
		}

		try {
			const draft = await upload.ensureDraft();
			const result = await submit.mutateAsync({
				draftId: draft.draftId,
				draftToken: draft.draftToken,
				topic: values.topic as SupportTopic,
				accountEmail: values.accountEmail || null,
				contactEmail: values.contactEmail,
				message: values.message,
				locale,
				serviceId: context?.serviceId ?? null,
				errorCode: context?.errorCode ?? null,
				policyVersion: policy.version,
				policySha256: policy.sha256,
				website: honeypot,
			});
			setTicketCode(result.ticketCode);
		} catch (cause) {
			// «Слишком часто» и «что-то сломалось» требуют от человека разных
			// действий, поэтому различаем их — но по коду, а не по тексту.
			setError(isTooManyRequests(cause) ? t("errors.tooOften") : t("errors.failed"));
		}
	};

	const topics = SIGNIN_SUPPORT_TOPICS.map((topic) => ({
		value: topic,
		label: t(`topics.${topic}`),
	}));

	return (
		<Dialog
			open={open}
			onOpenChange={close}
			title={ticketCode ? t("done.title") : t("title")}
			description={ticketCode ? undefined : t("description")}
			closeLabel={t("close")}
			width={560}
		>
			{ticketCode ? (
				<div className="flex flex-col gap-5">
					<p className="t-body text-fg-secondary">{t("done.body")}</p>
					<p className="t-num-sm text-fg-primary">{ticketCode}</p>
					<p className="t-meta text-fg-muted">{t("done.hint")}</p>
					<Button variant="second" onClick={() => close(false)}>
						{t("done.action")}
					</Button>
				</div>
			) : (
				<SupportForm
					topics={topics}
					labels={{
						topic: t("fields.topic"),
						topicPlaceholder: t("fields.topicPlaceholder"),
						accountEmail: t("fields.accountEmail"),
						accountEmailHint: t("fields.accountEmailHint"),
						contactEmail: t("fields.contactEmail"),
						message: t("fields.message"),
						messagePlaceholder: t("fields.messagePlaceholder"),
						submit: t("submit"),
						errorTopic: t("errors.topic"),
						errorContactEmail: t("errors.contactEmail"),
						errorMessage: t("errors.message"),
					}}
					consentLabel={
						<span className="t-meta text-fg-secondary">
							{t("consent.before")}{" "}
							<a
								href={policy?.url ?? "#"}
								target="_blank"
								rel="noreferrer"
								className="text-brand-text underline underline-offset-4"
							>
								{t("consent.link")}
							</a>
						</span>
					}
					files={{
						files: upload.items,
						onSelect: (files) => void upload.select(files),
						onRemove: upload.remove,
						accept: "image/*,video/*",
						label: t("files.label"),
						hint: t("files.hint"),
						removeLabel: t("files.remove"),
					}}
					defaults={{ accountEmail: context?.accountEmail ?? "" }}
					honeypot={{ name: "website", value: honeypot, onChange: setHoneypot }}
					onSubmit={(values) => void onSubmit(values)}
					pending={submit.isPending || upload.uploading}
					error={error}
				/>
			)}
		</Dialog>
	);
}
